import { MouseEvent, ReactNode } from 'react';

import { ApiUserEnvironmentOrgMini } from 'api/userEnvironment/types/ApiUserEnvironmentOrgMini';
import { DropdownChevron } from 'components/Dropdown/Chevron/DropdownChevron';
import { DropdownHeaderTrigger } from 'components/Dropdown/HeaderTrigger/DropdownHeaderTrigger';
import { FindAJobDropdown } from 'components/FindAJobDropdown/FindAJobDropdown';
import { IdealistLogo } from 'components/Logo/IdealistLogo';
import { PostOnIdealistDropdown } from 'components/PostOnIdealistDropdown/PostOnIdealistDropdown';
import { TakeActionDropdown } from 'components/TakeActionDropdown/TakeActionDropdown';
import { useIsClient } from 'hooks/useIsClient';
import { useViewportBreakpoint } from 'hooks/viewport/useViewportBreakpoint';

import {
  PageHeaderCartPillHolder,
  PageHeaderDesktopDonateNavLink,
  PageHeaderDesktopSearchHolder,
  PageHeaderHolder,
  PageHeaderLeft,
  PageHeaderLinksHolder,
  PageHeaderLoginHolder,
  PageHeaderLoginLink,
  PageHeaderLogoTrackedLink,
  PageHeaderMobileDonateButtonHolder,
  PageHeaderMobileDonateRouterButton,
  PageHeaderMobileLinksPlaceholder,
  PageHeaderMobileSearchHolder,
  PageHeaderMyAccountHolder,
  PageHeaderRight,
  PageHeaderTopSectionHolder,
} from './PageHeader.styled';

type Props = {
  userDisplayName: string | undefined;
  isUserLoaded: boolean;
  orgs: ApiUserEnvironmentOrgMini[];

  loginHref: string;
  onClickLogin: (event: MouseEvent<HTMLAnchorElement>) => void;

  myAccountDropdown: ReactNode;
  notificationsDropdown: ReactNode;
  cartPill: ReactNode;

  desktopSearch: ReactNode;
  mobileSearchDropdown: ReactNode;

  locale: UserLocale;
};

const donateLinkByLocale: Record<UserLocale, string> = {
  en: '/en/donate',
  pt: '/pt/doacao',
  es: '/es/dona',
};

export function PageHeader({
  userDisplayName,
  isUserLoaded,
  orgs,
  loginHref,
  onClickLogin,
  notificationsDropdown,
  myAccountDropdown,
  cartPill,
  desktopSearch,
  mobileSearchDropdown,
  locale,
}: Props) {
  const isLoggedIn = typeof userDisplayName !== 'undefined';
  const donateLink = donateLinkByLocale[locale];

  const isClient = useIsClient();
  const viewport = useViewportBreakpoint();
  const isDesktop = isClient && ['md', 'lg'].includes(viewport);

  return (
    <PageHeaderHolder data-qa-id="page-header">
      <PageHeaderTopSectionHolder>
        <PageHeaderLeft>
          <PageHeaderLogoTrackedLink
            href="/"
            title="Idealist"
            eventName="Idealist Logo Header"
            data-qa-id="header-logo-link"
          >
            <IdealistLogo withText height={{ mobile: 28, desktop: 36 }} />
          </PageHeaderLogoTrackedLink>

          <PageHeaderLinksHolder>
            <FindAJobDropdown
              locale={locale}
              renderTrigger={({ triggerText, triggerHref }) => (
                <DropdownHeaderTrigger
                  variant="grey"
                  qaId="page-header-find-a-job-dropdown-trigger"
                  asRouterLink={isDesktop ? { to: triggerHref } : undefined}
                >
                  {triggerText} <DropdownChevron />
                </DropdownHeaderTrigger>
              )}
            />

            <TakeActionDropdown
              locale={locale}
              renderTrigger={({ triggerText, triggerHref }) => (
                <DropdownHeaderTrigger
                  variant="grey"
                  qaId="page-header-take-action-dropdown-trigger"
                  asRouterLink={isDesktop ? { to: triggerHref } : undefined}
                  onClick={(event) => {
                    if (!isDesktop) return;

                    event.preventDefault();

                    // The Volop search page menu item needs a full page reload to work properly
                    window.location.href = triggerHref;
                  }}
                >
                  {triggerText} <DropdownChevron />
                </DropdownHeaderTrigger>
              )}
            />

            <PostOnIdealistDropdown
              locale={locale}
              renderTrigger={({ triggerText, triggerHref }) => (
                <DropdownHeaderTrigger
                  variant="grey"
                  qaId="page-header-post-on-idealist-dropdown-trigger"
                  asRouterLink={isDesktop ? { to: triggerHref } : undefined}
                >
                  {triggerText} <DropdownChevron />
                </DropdownHeaderTrigger>
              )}
            />

            <PageHeaderDesktopDonateNavLink to={donateLink}>
              {getText('Donate')}
            </PageHeaderDesktopDonateNavLink>
          </PageHeaderLinksHolder>
        </PageHeaderLeft>

        <PageHeaderRight>
          {isUserLoaded && !isLoggedIn && (
            <PageHeaderLoginHolder>
              <PageHeaderLoginLink
                href={loginHref}
                onClick={onClickLogin}
                data-qa-id="login-link"
                aria-label={getText('Log In')}
              >
                {getText('Log In')}
              </PageHeaderLoginLink>
            </PageHeaderLoginHolder>
          )}

          <PageHeaderMobileSearchHolder>
            {mobileSearchDropdown}
          </PageHeaderMobileSearchHolder>

          {isLoggedIn && (
            <PageHeaderMyAccountHolder>
              {myAccountDropdown}
            </PageHeaderMyAccountHolder>
          )}

          {isLoggedIn && notificationsDropdown}

          {orgs.length > 0 && (
            <PageHeaderCartPillHolder>{cartPill}</PageHeaderCartPillHolder>
          )}

          <PageHeaderMobileDonateButtonHolder>
            <PageHeaderMobileDonateRouterButton to={donateLink} size="small">
              {getText('Donate')}
            </PageHeaderMobileDonateRouterButton>
          </PageHeaderMobileDonateButtonHolder>
        </PageHeaderRight>
      </PageHeaderTopSectionHolder>

      <PageHeaderMobileLinksPlaceholder />

      {desktopSearch && (
        <PageHeaderDesktopSearchHolder data-qa-id="page-header-search-menu">
          {desktopSearch}
        </PageHeaderDesktopSearchHolder>
      )}
    </PageHeaderHolder>
  );
}
